import React, { FC } from 'react'
import styled, { keyframes } from 'styled-components'

// Mekome-branded spinner: the brand mark's three slanted capsules bobbing in a
// staggered wave. Drop-in replacement for the previous MUI CircularProgress
// wrapper — keeps the same import/centering and accepts `size` (px number or a
// CSS string like "2rem") and `color` ("white"/"inherit" render a mono spinner
// for use on dark/coloured buttons; anything else uses the brand two-tone).

const BRAND_DARK = '#2b32a8'
const BRAND_LIGHT = '#7b93d6'
// Nominal capsule height at scale 1; used to turn a px `size` into a scale.
const BASE_HEIGHT = 58

const wave = keyframes`
  0%, 70%, 100% { transform: rotate(20deg) translateY(0); opacity: 0.45; }
  35%           { transform: rotate(20deg) translateY(calc(-13px * var(--s))); opacity: 1; }
`
const fade = keyframes`
  0%, 100% { opacity: 0.45; }
  50%      { opacity: 1; }
`

const SpinnerRoot = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const Mark = styled.div<{ $color?: string }>`
  display: inline-flex;
  gap: calc(9px * var(--s));

  span {
    display: block;
    width: calc(20px * var(--s));
    height: calc(${BASE_HEIGHT}px * var(--s));
    border-radius: calc(10px * var(--s));
    transform: rotate(20deg);
    animation: ${wave} 1.1s ease-in-out infinite;
    will-change: transform, opacity;
  }
  span:nth-child(1) { background: ${p => p.$color || BRAND_DARK};  animation-delay: 0s; }
  span:nth-child(2) { background: ${p => p.$color || BRAND_LIGHT}; animation-delay: 0.14s; }
  span:nth-child(3) { background: ${p => p.$color || BRAND_DARK};  animation-delay: 0.28s; }

  @media (prefers-reduced-motion: reduce) {
    span { animation: ${fade} 1.2s ease-in-out infinite; }
  }
`

const toScale = (size?: number | string): number => {
  if (typeof size === 'number') return size / BASE_HEIGHT
  if (typeof size === 'string') {
    const n = parseFloat(size)
    if (isNaN(n)) return 40 / BASE_HEIGHT
    return (size.includes('rem') ? n * 16 : n) / BASE_HEIGHT
  }
  return 40 / BASE_HEIGHT
}

type SpinnerProps = {
  size?: number | string
  color?: string
  className?: string
  style?: React.CSSProperties
  [key: string]: any
}

const Spinner: FC<SpinnerProps> = ({ size, color, className, style }) => {
  const scale = toScale(size)
  const mono = color === 'white' ? '#ffffff' : color === 'inherit' ? 'currentColor' : undefined
  return (
    <SpinnerRoot className={className} style={style} role='status' aria-label='Loading'>
      <Mark $color={mono} style={{ '--s': scale } as React.CSSProperties}>
        <span /><span /><span />
      </Mark>
    </SpinnerRoot>
  )
}

export default Spinner
